import { useState, useEffect, useRef } from 'react';

// Pulls that start inside one of these never refresh the page: a modal's own
// list scrolls inside a fixed overlay while the page behind it sits at the top.
export const PTR_IGNORE_SELECTOR =
  '.modal-overlay, .payment-modal-overlay, .print-view, .print-overlay, [data-no-ptr]';

const scrollTop = () =>
  window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

// In-app pull-to-refresh. The installed app (manifest display: standalone) has
// no browser gesture, so this is the only refresh there. It calls the page's
// own loader; a full reload happens only when no onRefresh is given.
const usePullToRefresh = (onRefresh, options = {}) => {
  const {
    threshold = 60,
    resistance = 0.5,
    maxPull = 120,
  } = options;

  // State drives the indicator only. The gesture itself lives in refs, so the
  // listeners are registered once and never read a stale distance.
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const settingsRef = useRef({ threshold, resistance, maxPull });
  settingsRef.current = { threshold, resistance, maxPull };

  const gesture = useRef({ pulling: false, startX: 0, startY: 0, distance: 0, refreshing: false });

  useEffect(() => {
    const g = gesture.current;

    const setDistance = (d) => {
      g.distance = d;
      setPullDistance(d);
    };

    const stop = () => {
      g.pulling = false;
      setIsPulling(false);
    };

    const refresh = async () => {
      g.refreshing = true;
      setIsRefreshing(true);
      setDistance(settingsRef.current.threshold); // hold at the refresh position
      const callback = onRefreshRef.current;
      if (!callback) {
        window.location.reload();
        return;
      }
      try {
        await callback();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        g.refreshing = false;
        setIsRefreshing(false);
        setDistance(0);
      }
    };

    const handleTouchStart = (e) => {
      if (g.refreshing || e.touches.length !== 1) return;
      const target = e.target;
      if (target && target.closest && target.closest(PTR_IGNORE_SELECTOR)) return;
      if (scrollTop() > 0) return;
      g.pulling = true;
      g.startX = e.touches[0].clientX;
      g.startY = e.touches[0].clientY;
      g.distance = 0;
    };

    const handleTouchMove = (e) => {
      if (!g.pulling) return;
      const dx = e.touches[0].clientX - g.startX;
      const dy = e.touches[0].clientY - g.startY;
      if (dy === 0) return;
      // Upward, sideways, or the page has scrolled: not a pull.
      if (dy < 0 || Math.abs(dx) > dy || scrollTop() > 0) {
        stop();
        setDistance(0);
        return;
      }
      // Cancel from the first downward move so the browser bounce never takes over.
      if (e.cancelable) e.preventDefault();
      setIsPulling(true);
      const { resistance: r, maxPull: max } = settingsRef.current;
      setDistance(Math.min(dy * r, max));
    };

    const handleTouchEnd = () => {
      if (!g.pulling) return;
      stop();
      if (g.distance >= settingsRef.current.threshold) {
        refresh();
      } else {
        setDistance(0);
      }
    };

    const handleTouchCancel = () => {
      if (!g.pulling) return;
      stop();
      setDistance(0);
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, []);

  const PullToRefreshIndicator = () => (
    <div
      className="pull-to-refresh-indicator"
      style={{
        height: pullDistance,
        opacity: Math.min(pullDistance / threshold, 1),
        transition: isPulling ? 'none' : 'all 0.3s ease',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <div className={`ptr-spinner ${isRefreshing ? 'spinning' : ''}`}>
        {isRefreshing ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        ) : (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{
              transform: `rotate(${Math.min(pullDistance / threshold * 180, 180)}deg)`,
              transition: isPulling ? 'none' : 'transform 0.3s ease'
            }}
          >
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        )}
      </div>
      <span className="ptr-text">
        {isRefreshing ? 'Refreshing...' : pullDistance >= threshold ? 'Release to refresh' : 'Pull to refresh'}
      </span>
    </div>
  );

  return {
    isRefreshing,
    pullDistance,
    PullToRefreshIndicator,
  };
};

export default usePullToRefresh;
